// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ledgerAccountTypeSchema } from '@/account/api/types';
import {
  Currency,
  currencyAmountSchema,
  normalizedIso,
} from '@/currency/api/types';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import {
  fixedAssetsRollforwardDataSchema,
  inventoryRollforwardDataSchema,
} from '@/report/api/types';
import { sparsePersonSchema } from '@/sparse/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

// API Response Schemas

/**
 * Schema for a prior period adjustment from the API.
 * Represents the difference between current computed balances and prior locked balances.
 */
const priorPeriodAdjustmentSchema = z.object({
  accountType: ledgerAccountTypeSchema,
  accountName: z.string(),
  priorBalance: z.string(),
  currentBalance: z.string(),
  delta: z.string(),
});

export type PriorPeriodAdjustment = z.infer<typeof priorPeriodAdjustmentSchema>;

// Stays a string for its consumers, but an unparseable amount fails the response.
const journalLineAmountSchema = z
  .string()
  .refine((value) => parseCurrencyAmount(value) !== null, {
    message: 'Amount must be a valid number',
  });

const journalEntryLineItemResponseSchema = z.object({
  id: z.string(),
  key: z.string(),
  ledgerAccountType: ledgerAccountTypeSchema,
  ledgerAccountName: z.string(),
  description: z.string(),
  debit: journalLineAmountSchema,
  credit: journalLineAmountSchema,
});

/**
 * Schema for a journal entry type from the API.
 */
const journalEntryTypeSchema = z.enum([
  'INVENTORY_PURCHASES',
  'INVENTORY_SALE',
  'INVENTORY_SCRAP',
  'INVENTORY_TO_FIXED_ASSETS',
  'DEPRECIATION',
  'FIXED_ASSET_SALE',
  'FIXED_ASSET_SCRAP',
  'ADJUSTMENT',
  'ONBOARDING',
]);

export type JournalEntryType = z.infer<typeof journalEntryTypeSchema>;

/**
 * Canonical sort order for journal entry types.
 * Matches the order defined in journalEntryTypeSchema.
 */
const JOURNAL_ENTRY_TYPE_ORDER: Record<JournalEntryType, number> = {
  INVENTORY_PURCHASES: 0,
  INVENTORY_SALE: 1,
  INVENTORY_SCRAP: 2,
  INVENTORY_TO_FIXED_ASSETS: 3,
  DEPRECIATION: 4,
  FIXED_ASSET_SALE: 5,
  FIXED_ASSET_SCRAP: 6,
  ADJUSTMENT: 7,
  ONBOARDING: 8,
};

/**
 * Sorts journal entries by their entry type in the canonical display order.
 * This ensures consistent ordering across all views (close workflow, period review, etc.).
 */
export function sortJournalEntriesByType<
  T extends { entryType: JournalEntryType },
>(entries: T[]): T[] {
  return [...entries].sort(
    (a, b) =>
      JOURNAL_ENTRY_TYPE_ORDER[a.entryType] -
      JOURNAL_ENTRY_TYPE_ORDER[b.entryType]
  );
}

/**
 * Schema for a journal entry from the API.
 */
const journalEntryResponseSchema = z.object({
  id: z.string(),
  key: z.string(),
  entryType: journalEntryTypeSchema,
  externalId: z.string(),
  memo: z.string(),
  date: dateSchema,
  currency: normalizedIso(isoCurrencyCodeSchema),
  totalDebits: z.string(),
  totalCredits: z.string(),
  csvFileKey: z.string(),
  lineItems: z.array(journalEntryLineItemResponseSchema),
});

export type JournalEntryResponse = z.infer<typeof journalEntryResponseSchema>;

/**
 * Schema for an accounting period a close run or initial true-up has been created in.
 */
export const savedAccountingPeriodSchema = z.object({
  id: z.string(),
  key: z.string(),
  year: z.number(),
  month: z.number(),
});

export type SavedAccountingPeriod = z.infer<typeof savedAccountingPeriodSchema>;

/**
 * One currency's slice of a close run.
 */
export const closeRunCurrencyBucketSchema = z.object({
  currency: normalizedIso(isoCurrencyCodeSchema),
  inventoryRollforward: inventoryRollforwardDataSchema,
  fixedAssetRollforward: fixedAssetsRollforwardDataSchema,
  adjustments: z.array(priorPeriodAdjustmentSchema),
  journalEntries: z.array(journalEntryResponseSchema),
});

export type CloseRunCurrencyBucket = z.infer<
  typeof closeRunCurrencyBucketSchema
>;

/**
 * Schema for the close run response from the API.
 */
export const closeRunResponseSchema = z.object({
  id: z.string(),
  key: z.string(),
  period: savedAccountingPeriodSchema,
  createdAt: dateTimeSchema,
  createdBy: sparsePersonSchema,
  lockedAt: dateTimeSchema.nullable(),
  lockedBy: sparsePersonSchema.nullable(),
  lockedSignature: z.string().nullable(),
  lockedNotes: z.string().nullable(),
  excelFileKey: z.string(),
  byCurrency: z.array(closeRunCurrencyBucketSchema),
});

export type CloseRunResponse = z.infer<typeof closeRunResponseSchema>;

/**
 * Schema for a period's locked close, which covers every currency.
 */
export const periodCloseForAllCurrenciesSchema = z.object({
  closeRunId: z.string(),
  closeRunKey: z.string(),
  lockedAt: dateTimeSchema,
  lockedBy: sparsePersonSchema,
});

/**
 * Schema for one currency's locked initial true-up.
 */
export const initialTrueUpForOneCurrencySchema = z.object({
  currency: normalizedIso(isoCurrencyCodeSchema),
  closeRunId: z.string(),
  closeRunKey: z.string(),
  lockedAt: dateTimeSchema,
  lockedBy: sparsePersonSchema,
  accountingPeriod: savedAccountingPeriodSchema,
});

/**
 * Schema for a saved period with its locked close, if any, and its locked initial true-ups.
 */
export const savedAccountingPeriodActivityStatusSchema =
  savedAccountingPeriodSchema.extend({
    periodClose: periodCloseForAllCurrenciesSchema.nullable(),
    initialTrueUps: z.array(initialTrueUpForOneCurrencySchema),
  });

export type SavedAccountingPeriodActivityStatus = z.infer<
  typeof savedAccountingPeriodActivityStatusSchema
>;

/**
 * Schema for the org's latest locked period, its locked initial true-ups, and the asset currencies still missing one.
 */
export const initialTrueUpsForAllCurrenciesSchema = z.object({
  // The period a new initial true-up must land on; null before the org's first initial true-up.
  latestLockedPeriod: savedAccountingPeriodSchema.nullable(),
  // Sorted by currency.
  initialTrueUps: z.array(initialTrueUpForOneCurrencySchema),
  // Sorted asset currencies with no locked initial true-up.
  missingCurrencies: z.array(normalizedIso(isoCurrencyCodeSchema)),
});

export type InitialTrueUpsForAllCurrencies = z.infer<
  typeof initialTrueUpsForAllCurrenciesSchema
>;

// Initial true-up API Schemas

// One currency's opening GL balances ({amount, isoCode}); all three carry the same currency.
export interface InitialTrueUpCurrencyBalancesRequest {
  glInventory: Currency;
  glFixedAssets: Currency;
  glAccumulatedDepr: Currency;
}

export interface CreateInitialTrueUpRequest {
  year: number;
  month: number;
  // The route's currency; the backend rejects balances in any other.
  currency: IsoCurrencyCode;
  balances: InitialTrueUpCurrencyBalancesRequest;
  onboardingAdjustmentAccount: string;
}

/**
 * Schema for balances used during initial true-up.
 * These values represent the 3 account types' general ledger balances.
 */
const initialTrueUpBalancesSchema = z.object({
  inventory: currencyAmountSchema,
  fixedAssets: currencyAmountSchema,
  accumulatedDepreciation: currencyAmountSchema,
});

export type InitialTrueUpBalances = z.infer<typeof initialTrueUpBalancesSchema>;

/**
 * Schema for one currency's initial true-up close run response.
 */
export const initialTrueUpCloseRunResponseSchema = z.object({
  id: z.string(),
  key: z.string(),
  period: savedAccountingPeriodSchema,
  createdAt: dateTimeSchema,
  createdBy: sparsePersonSchema,
  lockedAt: dateTimeSchema.nullable(),
  lockedBy: sparsePersonSchema.nullable(),
  lockedSignature: z.string().nullable(),
  lockedNotes: z.string().nullable(),
  excelFileKey: z.string(),
  currency: normalizedIso(isoCurrencyCodeSchema),
  journalEntry: journalEntryResponseSchema,
  glBalances: initialTrueUpBalancesSchema,
  hardfinBalances: initialTrueUpBalancesSchema,
  deltas: initialTrueUpBalancesSchema,
});

export type InitialTrueUpCloseRunResponse = z.infer<
  typeof initialTrueUpCloseRunResponseSchema
>;

// UI-specific types

/**
 * Journal entry with UI-specific review state.
 * Extends the backend JournalEntryResponse type with frontend-only fields for tracking review status.
 *
 * Frontend-specific fields:
 * - reviewed: Tracks whether the user has reviewed this journal entry in the workflow
 */
export type JournalEntryWithReviewState = JournalEntryResponse & {
  reviewed: boolean;
};

/**
 * The current step in the accounting close workflow.
 */
export type AccountingCloseStep = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Tracks the review state across all journal entries in the workflow.
 * Used to generate the summary in the final confirmation step.
 *
 * Each entry category contains journal entries from the backend with added review tracking.
 */
export interface WorkflowReviewState {
  inventoryEntries: JournalEntryWithReviewState[];
  fixedAssetEntries: JournalEntryWithReviewState[];
  adjustmentEntries: JournalEntryWithReviewState[];
}
